import {StyleSheet, View} from 'react-native'

import {
  FlashBookDivider,
  FlashBookPageOrnament,
  FlashBookPageTexture,
} from '#/components/flashbooks/FlashBookPageDecor'
import {getFlashBookPlayPalette} from '#/components/flashbooks/FlashBookPlayTheme'
import {
  type FlashBookDesign,
  getFlashbookDesign,
} from '#/themes-flashbook/design'
import {getFlashbookTheme} from '#/themes-flashbook/registry'

/**
 * A miniature FlashBook *page*, not a decorative swatch.
 *
 * A creator choosing a theme is really asking "what will my book feel like?" —
 * a colour chip cannot answer that. This renders the same pieces the reader
 * does, at thumbnail scale: the theme's paper and ornament, a heading, body
 * text, a question with its answer rows in the theme's own shape and marker,
 * the read-aloud control and the progress language.
 *
 * It reads its geometry from the theme's design, so a theme that changes its
 * answer shape or page texture changes its preview automatically. Nothing here
 * is per-theme code.
 */
export function FlashBookThemePreview({themeId}: {themeId: string}) {
  const theme = getFlashbookTheme(themeId)
  const tok = theme.tokens
  const design = getFlashbookDesign(theme.id)
  const pal = getFlashBookPlayPalette(tok)

  return (
    <View
      style={[
        pv.sheet,
        {
          backgroundColor: tok.pageBg,
          borderRadius: design.page.radius,
          borderColor: tok.pageBorder,
          // Mirrors the reader: `open` prints on the shell and `panel` takes its
          // edge from the blocks themselves, so neither frames the sheet.
          borderWidth:
            design.block.mode === 'open' || design.block.mode === 'panel'
              ? 0
              : 1,
        },
      ]}>
      <FlashBookPageTexture texture={design.page.texture} color={tok.accent} />

      <View style={[pv.content, {zIndex: 1}]}>
        {/* Running head: progress in the theme's own language. */}
        <View style={pv.head}>
          <View style={pv.headBars}>
            <View style={[pv.kicker, {backgroundColor: tok.textMuted}]} />
            <MiniProgress style={design.progress.style} tokens={tok} />
          </View>
        </View>

        <FlashBookPageOrnament
          ornament={design.page.ornament}
          color={tok.accent}
        />

        {/* A heading and two lines of reading text. */}
        <View style={[pv.title, {backgroundColor: tok.textPrimary}]} />
        <View
          style={[pv.line, {backgroundColor: tok.textSecondary, width: '92%'}]}
        />
        <View
          style={[pv.line, {backgroundColor: tok.textSecondary, width: '74%'}]}
        />

        {/* Read-aloud control, in the theme's voice. */}
        <View style={pv.audioRow}>
          <MiniAudio design={design} accent={tok.accent} />
        </View>

        <FlashBookDivider
          divider={design.block.divider}
          color={tok.cardBorder}
          opacity={design.block.ruleOpacity}
          pad={4}
        />

        {/* A question, then two answers in the theme's shape and marker. */}
        <View style={[pv.question, {backgroundColor: tok.textPrimary}]} />
        <MiniOption design={design} pal={pal} marker={0} />
        <MiniOption design={design} pal={pal} marker={1} />
      </View>
    </View>
  )
}

/** The theme's progress treatment, drawn at thumbnail scale. */
function MiniProgress({
  style,
  tokens,
}: {
  style: FlashBookDesign['progress']['style']
  tokens: {progressTrack: string; progressFill: string}
}) {
  if (style === 'dots') {
    return (
      <View style={pv.dots}>
        {[0, 1, 2, 3, 4].map(i => (
          <View
            key={i}
            style={[
              pv.dot,
              {backgroundColor: tokens.progressFill, opacity: i < 2 ? 1 : 0.3},
            ]}
          />
        ))}
      </View>
    )
  }
  const squared = style === 'rule'
  return (
    <View
      style={[
        pv.track,
        squared && {height: 2, borderRadius: 0},
        {backgroundColor: tokens.progressTrack},
      ]}>
      <View
        style={[
          pv.fill,
          squared && {borderRadius: 0},
          {backgroundColor: tokens.progressFill, width: '40%'},
        ]}
      />
    </View>
  )
}

/** "Read to me" / "Listen", in the shape the theme gives it. */
function MiniAudio({
  design,
  accent,
}: {
  design: FlashBookDesign
  accent: string
}) {
  const shape =
    design.audio.style === 'plain'
      ? {
          borderRadius: 0,
          borderWidth: 0,
          paddingHorizontal: 0,
          paddingVertical: 0,
        }
      : design.audio.style === 'badge'
        ? {
            borderRadius: 3,
            borderWidth: 1,
            paddingHorizontal: 4,
            paddingVertical: 2,
          }
        : {
            borderRadius: 8,
            borderWidth: 1,
            paddingHorizontal: 5,
            paddingVertical: 2,
          }
  return (
    <View
      style={[
        pv.audio,
        shape,
        design.audio.style === 'plain'
          ? {}
          : {borderColor: accent + '66', backgroundColor: accent + '18'},
      ]}>
      <View style={[pv.play, {borderColor: accent}]} />
      <View style={[pv.audioBar, {backgroundColor: accent, width: 18}]} />
    </View>
  )
}

/** One answer row, drawn exactly as the theme would draw it. */
function MiniOption({
  design,
  pal,
  marker,
}: {
  design: FlashBookDesign
  pal: ReturnType<typeof getFlashBookPlayPalette>
  marker: number
}) {
  const {option, optionRadius, optionBorderWidth} = design.quiz

  const surface =
    option === 'row'
      ? {
          borderWidth: optionBorderWidth,
          borderColor: pal.border,
          backgroundColor: 'transparent',
          borderRadius: Math.min(optionRadius, 5),
        }
      : option === 'slab'
        ? {
            borderWidth: 0,
            borderLeftWidth: 3,
            borderLeftColor: pal.accent,
            backgroundColor: pal.surface,
            borderRadius: optionRadius,
          }
        : option === 'card'
          ? {
              borderWidth: optionBorderWidth,
              borderColor: pal.border,
              backgroundColor: pal.surface,
              borderRadius: optionRadius,
            }
          : {
              borderWidth: optionBorderWidth,
              borderColor: pal.border,
              backgroundColor: pal.surfaceAlt,
              borderRadius: optionRadius,
            }

  return (
    <View style={[pv.option, surface]}>
      <MiniMarker design={design} pal={pal} index={marker} />
      <View
        style={[
          pv.optLabel,
          {backgroundColor: pal.body, width: marker === 0 ? '62%' : '44%'},
        ]}
      />
    </View>
  )
}

/** The theme's answer marker: radio, letter, checkbox or dash. */
function MiniMarker({
  design,
  pal,
  index,
}: {
  design: FlashBookDesign
  pal: ReturnType<typeof getFlashBookPlayPalette>
  index: number
}) {
  const accent = index === 0
  const tone = accent ? pal.accent : pal.muted

  switch (design.quiz.marker) {
    case 'none':
      return null
    case 'letter':
      return (
        <View style={[pv.letter, {borderColor: tone}]}>
          <View style={[pv.letterDot, {backgroundColor: tone}]} />
        </View>
      )
    case 'box':
      return (
        <View style={[pv.box, {borderColor: tone}]}>
          {accent && <View style={[pv.boxDot, {backgroundColor: tone}]} />}
        </View>
      )
    case 'dash':
      return (
        <View
          style={{width: 10, height: 2, backgroundColor: tone, borderRadius: 1}}
        />
      )
    case 'radio':
    default:
      return (
        <View style={[pv.radio, {borderColor: tone}]}>
          {accent && <View style={[pv.radioDot, {backgroundColor: tone}]} />}
        </View>
      )
  }
}

const pv = StyleSheet.create({
  sheet: {flex: 1, overflow: 'hidden'},
  content: {flex: 1, padding: 9, gap: 4},
  head: {flexDirection: 'row', alignItems: 'center', marginBottom: 2},
  headBars: {flex: 1, gap: 3},
  kicker: {height: 3, width: 22, borderRadius: 1.5, opacity: 0.55},
  track: {height: 3, borderRadius: 1.5, overflow: 'hidden'},
  fill: {height: '100%', borderRadius: 1.5},
  dots: {flexDirection: 'row', gap: 2},
  dot: {width: 4, height: 4, borderRadius: 2},
  title: {height: 6, width: '58%', borderRadius: 2, opacity: 0.9, marginTop: 1},
  line: {height: 3, borderRadius: 1.5, opacity: 0.45},
  audioRow: {flexDirection: 'row', marginTop: 2},
  audio: {flexDirection: 'row', alignItems: 'center', gap: 3},
  play: {
    width: 0,
    height: 0,
    borderTopWidth: 3,
    borderBottomWidth: 3,
    borderLeftWidth: 5,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
  },
  audioBar: {height: 3, borderRadius: 1.5},
  question: {
    height: 5,
    width: '76%',
    borderRadius: 2,
    opacity: 0.85,
    marginTop: 1,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    padding: 5,
    marginTop: 2,
  },
  optLabel: {height: 3, borderRadius: 1.5, opacity: 0.5},
  radio: {
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {width: 3.5, height: 3.5, borderRadius: 2},
  letter: {
    width: 9,
    height: 9,
    borderRadius: 4.5,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  letterDot: {width: 3, height: 3, borderRadius: 1.5},
  box: {
    width: 8,
    height: 8,
    borderRadius: 1.5,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxDot: {width: 3.5, height: 3.5, borderRadius: 1},
})
